import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import { deleteFloatingIp, listFloatingIps } from './actions'
import { floatingIpsSelector } from './selectors'
// import { INetworkDetailsPageTabs } from './network-details/model'
import CreateFloatingIpModal from './CreateFloatingIpModal'
// import EditFloatingIpModal from './EditFloatingIpModal'
import useScopedPreferences from 'core/session/useScopedPreferences'
import AssignFloatingIpDialog from './AssignFloatingIpDialog'
import DetachFloatingIpDialog from './DetachFloatingIpDialog'
import CustomDeleteDialog from 'core/components/CustomDeleteDialog'
import { formatTimestampToLocal } from '../../vms/helpers'

type ModelDataKey = DataKeys.FloatingIps
type SelectorModel = ArrayElement<ReturnType<typeof floatingIpsSelector>>
type ActionParams = InferActionParams<typeof listFloatingIps>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'FloatingIps',
  listTablePrefs,
)

const searchTargets = ['floating_ip_address', 'id', 'fixed_ip_address', 'allocation']

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'floating_ip_address',
    label: 'Public IP Address',
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'dns_domain',
    label: 'DNS Zone',
    render: (val) => (val ? val : '-'),
  },
  {
    key: 'dns_name',
    label: 'DNS Name',
    render: (val) => (val ? val : '-'),
  },
  {
    key: 'fixed_ip_address',
    label: 'Private IP Address',
  },
  {
    key: 'allocation',
    label: 'Allocation',
    render: (val) => val?.name,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
  // {
  //   key: 'admin_state_up',
  //   label: 'Admin State',
  //   render: (value) => (value ? 'Up' : 'Down'),
  // },
  // {
  //   key: 'externalNetwork',
  //   label: 'External network',
  // },
]

export default function FloatingIpsListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { location } = useReactRouter()
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  const { message, loading, reload } = useListAction(listFloatingIps, {
    params,
  })
  const data = useAppSelector(floatingIpsSelector)

  const [selectedFloatingIp, setSelectedFloatingIp] = useState(null)
  const [showAssignFloatingIpDialog, setShowAssignFloatingIpDialog] = useState(false)
  const [showDetachFloatingIpDialog, setShowDetachFloatingIpDialog] = useState(false)

  const tenantFloatingIps = useMemo(() => {
    return data.filter((floatingIp) => {
      return floatingIp.project_id === currentTenant
    })
  }, [data, currentTenant])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (floatingIps) => !floatingIps[0]?.port_id,
      disabledTooltip: 'Select a Public IP that is not assigned to a VM',
      icon: 'plus-square',
      label: 'Assign IP',
      handleAction: (floatingIps) => {
        setSelectedFloatingIp(floatingIps[0] ?? null)
        setShowAssignFloatingIpDialog(true)
      },
    },
    {
      cond: (floatingIps) => !!floatingIps[0]?.port_id,
      disabledTooltip: 'Select a public IP that is currently assigned to a VM',
      icon: 'minus-square',
      label: 'Detach IP',
      handleAction: (floatingIps) => {
        setSelectedFloatingIp(floatingIps[0] ?? null)
        setShowDetachFloatingIpDialog(true)
      },
    },
  ]

  const DeletePublicIPsDialog = useCallback(
    (props) => (
      <CustomDeleteDialog
        {...props}
        deleteAction={deleteFloatingIp}
        title="Delete Public IP(s)"
        resourceLabel="Public IPs"
        getRowName={(row) => row.floating_ip_address}
      />
    ),
    [],
  )

  return (
    <>
      <DocumentMeta title="Public IPs" />
      <CreateFloatingIpModal addRoute={routeManager.routes.openstack.createFloatingIp} />
      {showAssignFloatingIpDialog && (
        <AssignFloatingIpDialog
          rows={[selectedFloatingIp]}
          onClose={() => setShowAssignFloatingIpDialog(false)}
        />
      )}
      {showDetachFloatingIpDialog && (
        <DetachFloatingIpDialog
          rows={[selectedFloatingIp]}
          onClose={() => setShowDetachFloatingIpDialog(false)}
        />
      )}
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.FloatingIps}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        data={tenantFloatingIps}
        columns={columns}
        DeleteDialogComponent={DeletePublicIPsDialog}
        addUrl={routeManager.routes.openstack.createFloatingIp.path()}
        addText="Create Public IP"
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteFloatingIp}
        batchActions={batchActions}
        // EditDialogComponent={EditFloatingIpModal}
        {...pick(listTablePrefs, params)}
        multiSelection
        nameProp="floating_ip_address"
      />
    </>
  )
}
